import { supabase } from './supabase';
import { applyDiscount } from './discounts';
import { sendSMS } from './messaging';
import { getBooking } from './bookings';
import { getCustomer } from './customers';
import toast from 'react-hot-toast';

export interface ReferralProgram {
  id: string;
  business_id: string;
  referrer_reward_amount: number;
  referee_reward_amount: number;
  is_active: boolean;
  referrer_sms_message?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Get referral program settings for a business
 */
export async function getReferralProgram(businessId: string): Promise<ReferralProgram | null> {
  if (!businessId) return null;

  try {
    const { data, error } = await supabase
      .from('referral_program')
      .select('*')
      .eq('business_id', businessId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned - referral program not set up yet
        return null;
      }
      console.error('Error fetching referral program:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error in getReferralProgram:', error);
    return null;
  }
}

/**
 * Check if a booking is fully paid
 */
export async function isBookingFullyPaid(bookingId: string): Promise<boolean> {
  try {
    const booking = await getBooking(bookingId);
    if (!booking) return false;

    const totalAmount = booking.total_amount || 0;
    const completedPayments = (booking.payments || []).filter(
      (p: any) => p.status === 'completed'
    );
    const totalPaid = completedPayments.reduce(
      (sum: number, payment: any) => sum + (payment.amount || 0),
      0
    );

    return totalPaid >= totalAmount && totalPaid > 0;
  } catch (error) {
    console.error('Error checking if booking is fully paid:', error);
    return false;
  }
}

/**
 * Apply referral discount to a booking
 * This should be called when a referral source is selected
 */
export async function applyReferralDiscount(
  bookingId: string,
  businessId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get referral program settings
    const referralProgram = await getReferralProgram(businessId);
    if (!referralProgram) {
      return { success: false, error: 'Referral program not configured' };
    }

    // Check if program is active (but we'll still apply discount if booking was created when it was active)
    // Actually, per user's requirement: "yes still process if deactivated"
    // So we'll apply the discount regardless of is_active status

    const refereeRewardAmount = parseFloat(String(referralProgram.referee_reward_amount)) || 0;
    if (refereeRewardAmount <= 0) {
      return { success: false, error: 'Referee reward amount is not set' };
    }

    // Apply discount using existing discount system
    await applyDiscount(
      bookingId,
      'Referral Discount',
      'fixed',
      refereeRewardAmount
    );

    return { success: true };
  } catch (error) {
    console.error('Error applying referral discount:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to apply referral discount'
    };
  }
}

/**
 * Process referral reward when booking becomes fully paid
 * This sends SMS to referrer and adds credit to their wallet
 */
export async function processReferralReward(
  bookingId: string,
  businessId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get booking details
    const booking = await getBooking(bookingId);
    if (!booking) {
      return { success: false, error: 'Booking not found' };
    }

    // Check if booking has referral source
    if (!booking.referral_source_customer_id) {
      return { success: false, error: 'No referral source for this booking' };
    }

    // Check if reward has already been processed
    if (booking.referral_reward_processed_at) {
      console.log('Referral reward already processed for booking:', bookingId);
      return { success: true }; // Already processed, not an error
    }

    // Check if booking is fully paid
    const isFullyPaid = await isBookingFullyPaid(bookingId);
    if (!isFullyPaid) {
      return { success: false, error: 'Booking is not fully paid yet' };
    }

    // Get referral program settings
    const referralProgram = await getReferralProgram(businessId);
    if (!referralProgram) {
      return { success: false, error: 'Referral program not configured' };
    }

    const referrerRewardAmount = parseFloat(String(referralProgram.referrer_reward_amount)) || 0;
    if (referrerRewardAmount <= 0) {
      return { success: false, error: 'Referrer reward amount is not set' };
    }

    // Get referrer customer details
    const referrerCustomer = await getCustomer(booking.referral_source_customer_id);
    if (!referrerCustomer) {
      return { success: false, error: 'Referrer customer not found' };
    }

    // Get booking customer name for SMS
    const bookingCustomerName = booking.customer
      ? `${booking.customer.first_name || ''} ${booking.customer.last_name || ''}`.trim() || 'your friend'
      : 'your friend';

    // Add credit to referrer's wallet
    const currentWallet = parseFloat(String(referrerCustomer.wallet || 0)) || 0;
    const newWalletBalance = currentWallet + referrerRewardAmount;

    const { error: walletUpdateError } = await supabase
      .from('customers')
      .update({ wallet: newWalletBalance })
      .eq('id', referrerCustomer.id);

    if (walletUpdateError) {
      console.error('Error updating referrer wallet:', walletUpdateError);
      return { success: false, error: 'Failed to update referrer wallet' };
    }

    // Log wallet transaction
    await supabase.from('wallet_transactions').insert({
      business_id: businessId,
      customer_id: referrerCustomer.id,
      amount: referrerRewardAmount,
      type: 'referral',
      description: `Referral reward for ${bookingCustomerName}`,
      reference_id: bookingId,
    });

    // Send SMS to referrer (if phone number exists)
    if (referrerCustomer.phone) {
      // Use custom message if set, otherwise use default
      const defaultMessage = `Thank you for referring ${bookingCustomerName}! We've added $${referrerRewardAmount.toFixed(2)} to your wallet for future bookings.`;
      const customMessage = referralProgram.referrer_sms_message || defaultMessage;
      
      // Replace placeholders in custom message
      const smsMessage = customMessage
        .replace(/{customer_name}/g, bookingCustomerName)
        .replace(/{reward_amount}/g, referrerRewardAmount.toFixed(2));
      
      const smsResult = await sendSMS({
        businessId,
        to: referrerCustomer.phone,
        message: smsMessage
      });

      if (!smsResult.success) {
        console.error('Failed to send referral SMS:', smsResult.error);
        // Don't fail the whole process if SMS fails - wallet credit is more important
      }
    } else {
      console.warn('Referrer customer has no phone number, skipping SMS');
    }

    // Mark reward as processed
    const { error: updateError } = await supabase
      .from('bookings')
      .update({ referral_reward_processed_at: new Date().toISOString() })
      .eq('id', bookingId);

    if (updateError) {
      console.error('Error marking referral reward as processed:', updateError);
      // Wallet and SMS are already done, so we'll continue
    }

    // Show success toast
    toast.success(
      `Referral reward processed: $${referrerRewardAmount.toFixed(2)} added to ${referrerCustomer.first_name} ${referrerCustomer.last_name}'s wallet`
    );

    return { success: true };
  } catch (error) {
    console.error('Error processing referral reward:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to process referral reward'
    };
  }
}

